import React, { memo, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { desktop, tablet } from 'services/ui';
import fontService, { FontWeight } from 'services/FontService';

import { ITab } from 'atoms';
import { LessonDescription } from 'molecules/LessonDescription';
import { LessonJournalContainer } from 'molecules/LessonJournal/LessonJournalContainer';
import { LessonZoomJoinContainer } from 'molecules/LessonZoomJoin';
import { MyLessonAttendance, Tabs, TasksTabs } from 'molecules';
import { LessonAttachments, TaskResults, LessonFinalTest } from 'organisms';
import { LessonStudentResources } from 'organisms/LessonStudentResources';
import { LessonWhiteboardContainer } from 'organisms/LessonWhiteboard';
import { HomeworkSubmitContainer } from 'organisms/HomeworkSubmit';
import { Colors, UserRole } from 'context';
import { AttachmentType, LessonType } from 'types';
import { PATHS } from 'router/config';
import { isNow, isTomorrow } from 'services/DateService';

export interface ILessonComponentProps {
  currentLesson?: LessonType | null;
  pathToDescription: string;
  pathToTasks: string;
  tabs: ITab[];
  hasLessonsToFinish: boolean;
  activeRole: UserRole;
  isFree: boolean;
  courseName: string;
  /**
   * Группа «без расписания»: убираем номер занятия, дату/время и производные
   * от них подписи. Доступ к содержимому при этом не меняется — все гейты
   * (материалы, доска, ДЗ, окно входа) работают как раньше.
   */
  hideSchedule?: boolean;
}

export const LessonComponent = memo(
  ({
    currentLesson,
    tabs,
    pathToTasks,
    pathToDescription,
    hasLessonsToFinish,
    activeRole,
    isFree,
    courseName,
    hideSchedule = false,
  }: ILessonComponentProps) => {
    const { t } = useTranslation();
    const isNowOrEnded = useMemo(() => {
      if (currentLesson) {
        return (
          currentLesson?.isEnded || isNow(currentLesson?.date) || isTomorrow(currentLesson?.date)
        );
      }
      return false;
    }, [currentLesson]);

    const showStudentAttachments = useMemo(() => {
      if (activeRole === UserRole.STUDENT) {
        return isNowOrEnded;
      }
      return activeRole === UserRole.METHODIST;
    }, [activeRole, isNowOrEnded]);

    const showTeacherTabs = useMemo(
      () => activeRole === UserRole.TEACHER && isNowOrEnded,
      [activeRole, isNowOrEnded],
    );
    return (
      <LessonWrapper>
        <LessonName>
          {hideSchedule ? (
            currentLesson?.name
          ) : (
            <>
              {currentLesson?.index}. {currentLesson?.name}
            </>
          )}
        </LessonName>
        <LessonZoomJoinStyled
          currentLesson={currentLesson}
          activeRole={activeRole}
          hideSchedule={hideSchedule}
        />
        <TabsStyled tabs={tabs} />
        <Separator />
        <ContentWrapper>
          <Routes>
            <Route path="/" element={<Navigate to={pathToDescription} replace />} />
            <Route
              path={PATHS.lessonDescription}
              element={
                <>
                  {/* ТЗ 0026: собственная отметка студента по этому занятию.
                      Список группы студенту не показываем нигде. */}
                  {activeRole === UserRole.STUDENT && (
                    <MyLessonAttendance lessonId={currentLesson?.id || ''} />
                  )}
                  <LessonDescription
                    key={currentLesson?.id}
                    videoSrc={currentLesson?.video}
                    description={currentLesson?.lmsDescription || `${t('lesson.noDescription')}`}
                    time={hideSchedule ? '' : currentLesson?.time || ''}
                    isFree={isFree}
                  />
                </>
              }
            />
            <Route
              path={PATHS.lessonStudentAttachments}
              element={
                <LessonAttachments
                  lessonId={currentLesson?.id || ''}
                  type={AttachmentType.student}
                  showAttachments={showStudentAttachments}
                />
              }
            />
            <Route path={PATHS.lessonStudentResources} element={<LessonStudentResources />} />
            <Route
              path={PATHS.lessonFinalTest}
              element={<LessonFinalTest courseName={courseName} />}
            />
            <Route
              path={PATHS.lessonTeacherAttachments}
              element={
                <LessonAttachments
                  lessonId={currentLesson?.id || ''}
                  type={AttachmentType.teacher}
                  showAttachments
                />
              }
            />
            <Route
              path={PATHS.lessonWhiteboard}
              element={<LessonWhiteboardContainer lessonId={currentLesson?.id || ''} />}
            />
            <Route
              path={PATHS.lessonHomework}
              element={
                // key: the route path is identical for every lesson, so
                // without it React reuses the instance when the student
                // switches lessons in the sidebar and local state survives —
                // a lingering `replacing` would offer to resubmit (and reset
                // the grade of) a lesson the student never touched.
                <HomeworkSubmitContainer
                  key={currentLesson?.id}
                  lessonId={currentLesson?.id || ''}
                  // ТЗ 0026: карточка ДЗ ссылается на учебник, а вкладка
                  // «Материалы» у студента открывается только накануне
                  // занятия. Блокировку владелец решил не снимать, поэтому
                  // карточка должна назвать дату, а не обещать недоступное.
                  textbookAvailableFrom={
                    activeRole === UserRole.STUDENT && !isNowOrEnded
                      ? currentLesson?.date || null
                      : null
                  }
                  hideDates={hideSchedule}
                />
              }
            />
            <Route
              path={PATHS.lessonJournal}
              element={
                <LessonJournalContainer
                  isFinished={Boolean(currentLesson?.finished)}
                  comment={currentLesson?.comment || ''}
                  isEnded={Boolean(currentLesson?.isEnded)}
                  hasLessonsToFinish={hasLessonsToFinish}
                  showJournal={showTeacherTabs}
                />
              }
            />
            {currentLesson && (
              <>
                <Route
                  path={`${PATHS.lessonTasks}/*`}
                  element={
                    <TasksTabs
                      currentLesson={currentLesson}
                      pathToTasks={pathToTasks}
                      showTasks={showTeacherTabs}
                    />
                  }
                />
                <Route
                  path={PATHS.lessonResults}
                  element={<TaskResults lessonsId={currentLesson.id} activeRole={activeRole} />}
                />
              </>
            )}
          </Routes>
        </ContentWrapper>
      </LessonWrapper>
    );
  },
);

const LessonWrapper = styled.div`
  width: 100%;

  ${desktop} {
    width: 700px;
  }
`;

const LessonName = styled.p`
  margin: 0;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    ${fontService.getFont({ size: 24, lineHeight: 150, weight: FontWeight.bold })}
  }

  ${desktop} {
    ${fontService.getFont({ size: 32, lineHeight: 138, weight: FontWeight.bold })}
  }
`;

const LessonZoomJoinStyled = styled(LessonZoomJoinContainer)`
  margin-top: 16px;
`;

const TabsStyled = styled(Tabs)`
  margin-top: 16px;
`;

const Separator = styled.div`
  height: 1px;
  background-color: ${Colors.system_gray};
`;

const ContentWrapper = styled.div`
  margin-top: 20px;

  ${tablet} {
    margin-top: 24px;
  }

  ${desktop} {
    margin-top: 36px;
  }
`;
